import React, { useState, useEffect } from 'react';
import API from '../../api/api';
import Skeleton from '@material-ui/lab/Skeleton';
import { getImageUrl, getOriginalImageUrl } from '../../utils/imageUtils';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/dist/styles.css';
import PageLayout from '../../components/PageLayout/PageLayout';

const garageApi = new API();

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
  },
  section: {
    padding: theme.spacing(8, 0),
    backgroundColor: '#ffffff',
  },
  title: {
    fontWeight: 700,
    fontSize: '2.5rem',
    marginBottom: theme.spacing(4),
    color: '#1b4995',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      marginBottom: theme.spacing(3),
    },
  },
  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: theme.spacing(1.5),
    },
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    paddingBottom: '100%',
    overflow: 'hidden',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
    },
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  videoWrapper: {
    position: 'relative',
    width: '100%',
    paddingBottom: '100%',
    overflow: 'hidden',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    backgroundColor: '#000',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
    },
  },
  video: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  videoIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: 'white',
    fontSize: '3rem',
    zIndex: 1,
  },
}));

export default function GalleryPage(props) {
  const classes = useStyles();
  const { configuration } = props;
  const [allImages, setAllImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    // Lade alle neuesten Bilder
    garageApi.openEndpoints.gallery.getLatestImages(500, 0)
      .then((getRequest) => {
        try {
          const images = Array.isArray(getRequest.data) ? getRequest.data : [];
          setAllImages(images);
        } catch(e) {
          console.log(e);
          setAllImages([]);
        } finally {
          setImagesLoading(false);
        }
      })
      .catch(() => {
        setAllImages([]);
        setImagesLoading(false);
      });
  }, []);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Bereite Lightbox-Slides vor
  const lightboxSlides = allImages.map((image) => {
    if (image.is_video) {
      return {
        type: 'video',
        sources: [
          {
            src: getOriginalImageUrl(image.image) || '',
            type: 'video/mp4',
          },
        ],
      };
    } else {
      return {
        src: getOriginalImageUrl(image.image) || getImageUrl(image.image, 'large') || '',
        alt: image.name || 'Galerie Bild',
      };
    }
  });

  return (
    <PageLayout configuration={configuration} title="Galerie">
      <section className="spg-section">
              {imagesLoading ? (
                <Box className={classes.imageGrid}>
                  {Array.from({ length: 12 }, (_, i) => (
                    <Box key={i}>
                      <Skeleton variant="rect" width="100%" style={{ paddingBottom: '100%', borderRadius: '12px' }} />
                    </Box>
                  ))}
                </Box>
              ) : allImages.length > 0 ? (
                <>
                  <Box className={classes.imageGrid}>
                    {allImages.map((image, index) => (
                      <Box
                        key={image.unikey || image.id || index}
                        className={image.is_video ? classes.videoWrapper : classes.imageWrapper}
                        onClick={() => openLightbox(index)}
                      >
                        {image.is_video ? (
                          <>
                            <video
                              src={getImageUrl(image.image, 'small') || getOriginalImageUrl(image.image) || ''}
                              className={classes.video}
                              muted
                              playsInline
                            />
                            <Box className={classes.videoIcon}>▶</Box>
                          </>
                        ) : (
                          <img
                            src={getImageUrl(image.image, 'small') || getImageUrl(image.image, 'thumbnail') || ''}
                            alt={image.name || 'Galerie Bild'}
                            className={classes.image}
                            loading="lazy"
                          />
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Lightbox
                    open={lightboxOpen}
                    close={() => setLightboxOpen(false)}
                    index={lightboxIndex}
                    slides={lightboxSlides}
                    render={{
                      slide: ({ slide }) => {
                        if (slide.type === 'video') {
                          return (
                            <video
                              src={slide.sources[0].src}
                              controls
                              autoPlay
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100vh',
                                objectFit: 'contain',
                              }}
                            />
                          );
                        }
                        return null;
                      },
                    }}
                  />
                </>
              ) : (
                <p className="spg-lead">Noch keine Bilder in der Galerie vorhanden.</p>
              )}
      </section>
    </PageLayout>
  );
}
